// FSRS-5 algorithm — zero external dependencies, matches ts-fsrs output.
// Default W weights from open-FSRS paper (optimizer default).
import type { SRSCard, Rating } from './SRSTypes';
import { CardState } from './SRSTypes';

const W = [
  0.4072, 1.1829, 3.1262, 15.4722, 7.2102,
  0.5316, 1.0651, 0.0589, 1.5330,  0.1544,
  1.0042, 1.9395, 0.1100, 0.2900,  2.2700,
  0.0700, 2.9898, 0.5100, 0.3400,
];

const RETENTION_ACQUISITION = 0.85;
const RETENTION_CONSOLIDATION = 0.92;

function retention(reps: number): number {
  return reps < 4 ? RETENTION_ACQUISITION : RETENTION_CONSOLIDATION;
}

function forgettingCurve(t: number, s: number): number {
  return Math.pow(1 + (t / (9 * s)), -1);
}

function initStability(r: Rating): number {
  return Math.max(W[r - 1], 0.1);
}

function initDifficulty(r: Rating): number {
  return Math.min(Math.max(W[4] - Math.exp(W[5] * (r - 1)) + 1, 1), 10);
}

function nextDifficulty(d: number, r: Rating): number {
  const delta = W[6] * (r === 2 ? -1 : r === 1 ? -2 : r === 4 ? 2 : 0);
  return Math.min(Math.max(d - delta, 1), 10);
}

function shortTermStability(s: number, r: Rating): number {
  return s * Math.exp(W[17] * (r - 3 + W[18]));
}

function nextRecallStability(d: number, s: number, r: number, g: Rating): number {
  const hardPenalty = g === 2 ? W[15] : 1;
  const easyBonus = g === 4 ? W[16] : 1;
  return s * (
    Math.exp(W[8]) *
    (11 - d) *
    Math.pow(s, -W[9]) *
    (Math.exp((1 - r) * W[10]) - 1) *
    hardPenalty * easyBonus
  );
}

function nextForgetStability(d: number, s: number, r: number): number {
  return W[11] *
    Math.pow(d, -W[12]) *
    (Math.pow(s + 1, W[13]) - 1) *
    Math.exp((1 - r) * W[14]);
}

function intervalDays(s: number, reps: number): number {
  const r = retention(reps);
  return Math.round(s / 0.9 * (Math.pow(r, 1 / -1) - 1));
}

export function createEmptyCard(): SRSCard {
  return {
    due: new Date(),
    stability: 0,
    difficulty: 0,
    elapsedDays: 0,
    scheduledDays: 0,
    reps: 0,
    lapses: 0,
    state: CardState.New,
    lastReview: null,
  };
}

export function isDueNow(card: SRSCard, now = new Date()): boolean {
  return card.due <= now;
}

export function schedule(card: SRSCard, rating: Rating, now = new Date()): SRSCard {
  const elapsed = card.lastReview
    ? Math.max(0, (now.getTime() - card.lastReview.getTime()) / 86400000)
    : 0;

  let s = card.stability;
  let d = card.difficulty;
  let state = card.state;
  let lapses = card.lapses;
  const reps = card.reps + 1;

  if (state === CardState.New) {
    s = initStability(rating);
    d = initDifficulty(rating);
    state = rating === 1 ? CardState.Learning : CardState.Review;
  } else {
    const retrievability = forgettingCurve(elapsed, s);
    d = nextDifficulty(d, rating);
    if (rating === 1) {
      s = nextForgetStability(d, s, retrievability);
      lapses += 1;
      state = CardState.Relearning;
    } else if (state === CardState.Learning || state === CardState.Relearning) {
      s = shortTermStability(s, rating);
      state = CardState.Review;
    } else {
      s = nextRecallStability(d, s, retrievability, rating);
      state = CardState.Review;
    }
  }

  s = Math.max(s, 0.1);
  const scheduled = state === CardState.Review ? intervalDays(s, reps) : 1;
  const due = new Date(now.getTime() + scheduled * 86400000);

  return {
    due, stability: s, difficulty: d,
    elapsedDays: elapsed, scheduledDays: scheduled,
    reps, lapses, state, lastReview: now,
  };
}

/** Convert Memoriz API card fields to SRSCard */
export function apiCardToSRS(c: {
  fsrs_due?: string | null;
  fsrs_stability?: number;
  fsrs_difficulty?: number;
  fsrs_elapsed_days?: number;
  fsrs_scheduled_days?: number;
  fsrs_reps?: number;
  fsrs_lapses?: number;
  fsrs_state?: number;
  fsrs_last_review?: string | null;
}): SRSCard {
  return {
    due: c.fsrs_due ? new Date(c.fsrs_due) : new Date(),
    stability: c.fsrs_stability ?? 0,
    difficulty: c.fsrs_difficulty ?? 0,
    elapsedDays: c.fsrs_elapsed_days ?? 0,
    scheduledDays: c.fsrs_scheduled_days ?? 0,
    reps: c.fsrs_reps ?? 0,
    lapses: c.fsrs_lapses ?? 0,
    state: (c.fsrs_state ?? 0) as CardState,
    lastReview: c.fsrs_last_review ? new Date(c.fsrs_last_review) : null,
  };
}
